import { supabase } from '../supabaseClient';
import { buildOrderCode } from '../admin/ordersPage';

export const SUPPORT_EMAIL_TYPES = {
  REFUND_APPROVED: 'refund_approved',
  REFUND_DENIED: 'refund_denied',
  ORDER_CONFIRMED: 'order_confirmed',
  EVENT_CANCELLED: 'event_cancelled',
};

const siteBaseUrl = () => {
  const fromEnv = String(process.env.REACT_APP_SITE_URL || '').trim().replace(/\/$/, '');
  if (fromEnv) return fromEnv;
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'https://www.echojham.site';
};

export const buildSupportEmailUrls = (base = siteBaseUrl()) => ({
  profileUrl: `${base}/profile?section=support`,
  ticketsUrl: `${base}/tickets`,
});

const normalizeEmail = (value) => {
  const email = String(value || '').trim();
  if (!email || email === '-' || !email.includes('@')) return '';
  return email;
};

const formatSeatLabels = (seatLabels) => {
  const labels = Array.isArray(seatLabels) ? seatLabels : [seatLabels];
  return labels.map((label) => String(label || '').trim()).filter(Boolean);
};

const normalizePdfTickets = (tickets) => {
  if (!Array.isArray(tickets)) return [];
  return tickets
    .filter((row) => row && typeof row === 'object')
    .map((row) => ({
      qr_code: String(row.qr_code || row.qrCode || '').trim(),
      ticket_id: row.ticket_id ?? row.ticketId ?? row.id ?? '',
      attendee_name: String(row.attendee_name || row.attendeeName || '').trim() || 'Guest',
      attendee_email: String(row.attendee_email || row.attendeeEmail || '').trim(),
      tier_name: String(row.tier_name || row.tierName || '').trim(),
      section: String(row.section || row.seat_area || '').trim(),
      seat_label: String(row.seat_label || row.seatLabel || '').trim(),
      price: Number.isFinite(Number(row.price)) ? Number(row.price) : 0,
    }))
    .filter((row) => row.qr_code || row.ticket_id !== '' || row.seat_label);
};

export const buildOrderConfirmedPayload = ({
  toEmail,
  orderId,
  orderCode,
  eventTitle = '',
  eventDate = '',
  eventVenue = '',
  eventAddress = '',
  policyText = '',
  ticketCount = 0,
  seatLabels = [],
  tickets = [],
  paymentOrderId = null,
  baseUrl,
} = {}) => {
  const urls = buildSupportEmailUrls(baseUrl);
  const pdfTickets = normalizePdfTickets(tickets);
  const address = String(eventAddress || '').trim();
  const policy = String(policyText || '').trim();
  return {
    type: SUPPORT_EMAIL_TYPES.ORDER_CONFIRMED,
    to_email: normalizeEmail(toEmail),
    order_code: orderCode || buildOrderCode(orderId || paymentOrderId),
    event_title: String(eventTitle || '').trim() || null,
    event_date: String(eventDate || '').trim() || null,
    event_venue: String(eventVenue || '').trim() || null,
    ...(address ? { event_address: address } : {}),
    ...(policy ? { policy_text: policy } : {}),
    ticket_count: Number(ticketCount) || formatSeatLabels(seatLabels).length || pdfTickets.length || null,
    seat_labels: formatSeatLabels(seatLabels),
    ...(pdfTickets.length ? { tickets: pdfTickets } : {}),
    payment_order_id: paymentOrderId || orderId || null,
    tickets_url: urls.ticketsUrl,
    profile_url: urls.profileUrl,
  };
};

export const buildEventCancelledPayload = ({
  toEmail,
  eventTitle = '',
  eventDate = '',
  eventVenue = '',
  ticketCount = 0,
  cancellationReason = '',
  baseUrl,
} = {}) => {
  const urls = buildSupportEmailUrls(baseUrl);
  return {
    type: SUPPORT_EMAIL_TYPES.EVENT_CANCELLED,
    to_email: normalizeEmail(toEmail),
    event_title: String(eventTitle || '').trim() || null,
    event_date: String(eventDate || '').trim() || null,
    event_venue: String(eventVenue || '').trim() || null,
    ticket_count: Number(ticketCount) || null,
    cancellation_reason: String(cancellationReason || '').trim() || null,
    tickets_url: urls.ticketsUrl,
    profile_url: urls.profileUrl,
  };
};

const invokeSupportEmail = async (payload, client, label) => {
  const { data, error } = await client.functions.invoke('send-support-email', {
    body: payload,
  });

  if (error) {
    console.error(`${label} failed:`, error);
    return { ok: false, error: error.message || `Unable to send ${label}.` };
  }

  if (data && data.ok === false) {
    console.error(`${label} rejected:`, data);
    return { ok: false, error: data.error || `Unable to send ${label}.` };
  }

  return { ok: true, data };
};

export const resolveBuyerEmailForNotify = async ({
  userId,
  fallbackEmail,
  client = supabase,
} = {}) => {
  const fallback = normalizeEmail(fallbackEmail);
  if (!userId) return fallback;

  const { data } = await client
    .from('users')
    .select('email')
    .eq('id', userId)
    .maybeSingle();

  return normalizeEmail(data?.email) || fallback;
};

export const notifyBuyerRefundUpdate = async ({
  type,
  toEmail,
  orderId,
  orderCode,
  eventTitle = '',
  adminNote = '',
  supportTicketId = null,
  paymentOrderId = null,
  client = supabase,
} = {}) => {
  const normalizedType = String(type || '').trim();
  if (
    normalizedType !== SUPPORT_EMAIL_TYPES.REFUND_APPROVED
    && normalizedType !== SUPPORT_EMAIL_TYPES.REFUND_DENIED
  ) {
    throw new Error('Invalid refund email type.');
  }

  const email = normalizeEmail(toEmail);
  if (!email) {
    console.warn('Skipping refund email: missing buyer email.');
    return { ok: false, skipped: true, reason: 'missing_email' };
  }

  const urls = buildSupportEmailUrls();
  const payload = {
    type: normalizedType,
    to_email: email,
    order_code: orderCode || buildOrderCode(orderId || paymentOrderId),
    event_title: String(eventTitle || '').trim() || null,
    admin_note: String(adminNote || '').trim() || null,
    support_ticket_id: supportTicketId || null,
    payment_order_id: paymentOrderId || orderId || null,
    profile_url: urls.profileUrl,
    tickets_url: urls.ticketsUrl,
  };

  return invokeSupportEmail(payload, client, 'Refund email notify');
};

export const notifyBuyerOrderConfirmed = async ({
  client = supabase,
  ...details
} = {}) => {
  const payload = buildOrderConfirmedPayload(details);
  if (!payload.to_email) {
    console.warn('Skipping order confirmation email: missing buyer email.');
    return { ok: false, skipped: true, reason: 'missing_email' };
  }
  return invokeSupportEmail(payload, client, 'Order confirmation email');
};

export const notifyBuyerEventCancelled = async ({
  client = supabase,
  ...details
} = {}) => {
  const payload = buildEventCancelledPayload(details);
  if (!payload.to_email) {
    console.warn('Skipping cancellation email: missing buyer email.');
    return { ok: false, skipped: true, reason: 'missing_email' };
  }
  return invokeSupportEmail(payload, client, 'Cancellation email');
};
